/**
 * Simular que un contacto respondió (sólo con el transporte simulado). Sirve para recorrer la baja
 * de punta a punta sin un teléfono: «Responder BAJA» tiene que terminar en la lista de no
 * contactar, igual que una respuesta real.
 *
 * El número se tipea completo: la bitácora muestra los números enmascarados y no hay de dónde
 * sacarlos (a propósito). Con el transporte `twilio` el componente no se muestra, y el backend
 * igual responde 409 `SOLO_SIMULADO`.
 */

import { useId, useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import type { RespuestaEntrante, SimularInboundResultado } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";

const ATRIBUCION: Record<RespuestaEntrante["atribucion"], string> = {
  respuesta_a_mensaje: "respuesta a un mensaje de la campaña",
  ultimo_envio_7d: "atribuida al último envío de los últimos 7 días",
  sin_atribuir: "sin campaña",
};

interface Props {
  destinosSugeridos: string[];
  onHecho: () => void;
}

export function SimularRespuesta({ destinosSugeridos, onHecho }: Props) {
  const { modo, config } = useCuentaOficial();
  const idNumero = useId();
  const idLista = useId();
  const idTexto = useId();
  const [numero, setNumero] = useState(destinosSugeridos[0] || "");
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  if (modo !== "simulated") return null;
  const botonBaja = config?.botonBaja || "No quiero más";

  const simular = async (s: { body?: string; buttonPayload?: string; buttonText?: string }) => {
    const from = numero.trim();
    if (!from) { setMsg({ ok: false, texto: "Escribí el número completo de un contacto de prueba." }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const l = leerRespuesta<SimularInboundResultado>(await oficialApi.simularInbound({ from, ...s }));
      if (!l.ok) { setMsg({ ok: false, texto: l.error || textoError(l) }); return; }
      setMsg({
        ok: true,
        texto: l.data.esBaja ? "Se registró como baja." : `Se guardó la respuesta (${ATRIBUCION[l.data.atribucion] || l.data.atribucion}).`,
      });
      if (s.body && s.body !== "BAJA") setTexto("");
      onHecho();
    } catch (e) {
      const x = leerError(e);
      setMsg({ ok: false, texto: x.code === "SOLO_SIMULADO" ? "Sólo se puede simular con el transporte simulado." : textoError(x) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 space-y-3" aria-label="Simular una respuesta">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
        <FlaskConical size={15} /> Simular una respuesta (modo simulado)
      </p>
      <div>
        <label htmlFor={idNumero} className="text-xs font-semibold text-slate-600">Número</label>
        <input id={idNumero} list={idLista} value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="+54 9 11 5544-3322"
          className="mt-1 w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        <datalist id={idLista}>{destinosSugeridos.map((d) => <option key={d} value={d} />)}</datalist>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => void simular({ body: "BAJA" })}
          className="h-9 px-3 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">Responder BAJA</button>
        <button type="button" disabled={busy} onClick={() => void simular({ buttonPayload: "BAJA", buttonText: botonBaja })}
          className="h-9 px-3 rounded-lg border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50">Tocar «{botonBaja}»</button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <label htmlFor={idTexto} className="text-xs font-semibold text-slate-600">Texto de la respuesta</label>
          <input id={idTexto} value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={1000} placeholder="Quiero pedir"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        <button type="button" disabled={busy || !texto.trim()} onClick={() => void simular({ body: texto.trim() })}
          className="h-9 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy && <Loader2 size={12} className="animate-spin" />} Responder texto
        </button>
      </div>
      {msg && <p className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-700"}`} role="status">{msg.texto}</p>}
    </section>
  );
}

export default SimularRespuesta;
